// tslint:disable: cyclomatic-complexity
import React from 'react';
import { Cancel as CancelIcon, CheckCircle as CheckCircleIcon } from '@markinson/uicomponents-v2/SvgIcons';
import IFastEntryProps, { ILastChangedField, IFastEntryHandle, IProductDetails, IFastEntryDetails } from './FastEntry.properties';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import NumericField from '@markinson/uicomponents-v2/NumericField';
import { isNull } from 'utils/utils';
import ProductLookup from 'components/common/ProductLookup';
import classNames from 'classnames';
import { IWorksaleFastLineDetailFacade } from 'api/swaggerTypes';
import ITextFieldHandle from '@markinson/uicomponents-v2/TextField/TextField.handle';
import ExtendedTextField from '@markinson/uicomponents-v2/ExtendedTextField';
import LoadingButton from 'components/common/LoadingButton';
import { ENTER_KEY } from 'utils/constants';
import { useElementSize } from 'utils/hooks';
import { RESET_WAIT, MINIMUMSIZESCREEN } from './FastEntry.constants';
import { useFastEntryState, useFastEntryDispatch } from './FastEntry.context';
import { FastEntryActionTypes } from './FastEntry.actions';
import BasicLookupActionField from 'components/FormView/Fields/BasicLookupActionField/BasicLookupActionField';
import { IBasicLookupFieldHandle } from '@markinson/uicomponents-v2/BasicLookupField/BasicLookup.properties';
import CustomerPricingLookup from '../CustomerPricingLookup';

const FocusFieldTimeout = 200;

const FastEntry = (props: IFastEntryProps, ref: React.Ref<IFastEntryHandle>): JSX.Element => {
    const { classes, config, handlers } = props;
    const contextDispatch = useFastEntryDispatch();
    const {
        isLoading,
        quantity,
        price,
        product,
        purchaseOrder,
        packageId,
        matchType,
        productDisplay,
        productDescription,
        nonProductFieldsDisabled,
        fastLineDetails,
        lastChangedField,
        priceDisabled,
        isInvalidProduct,
        apply,
    } = useFastEntryState();

    const { width } = useElementSize();
    const quantityRef = React.useRef<ITextFieldHandle>();
    let priceRef = React.useRef<HTMLInputElement>();
    const productRef = React.useRef<ITextFieldHandle>();
    const purchaseRef = React.useRef<IBasicLookupFieldHandle>();
    const priceLookupRef = React.useRef<IBasicLookupFieldHandle>();
    const isDirtyPurchaseRefvalue = React.useRef({ isTouched: false, isDirty: false, value: '' });

    // Retrieve new price/quantity if product/quantity changed.
    async function handleFastEntryFieldChanged(details: IFastEntryDetails, field?: ILastChangedField): Promise<IWorksaleFastLineDetailFacade | void> {
        try {
            if (config.enableProductValidation && field === 'product' && details.ProductId) {
                const status = await handlers.isProductValid(details.ProductId);
                contextDispatch({
                    [FastEntryActionTypes.setIsInvalidProduct]: !status,
                });

                if (!status) return;
            }

            if (!details.ProductId) return null;

            contextDispatch({
                [FastEntryActionTypes.setLoading]: true,
                [FastEntryActionTypes.setLastChangedField]: field,
            });
            const response = await handlers.onFastEntryFieldChanged({ ...details, field });

            if (response && response?.Status) {
                contextDispatch({
                    [FastEntryActionTypes.setFastLineDetails]: response.FastLineDetails,
                });
                if (quantity !== response.FastLineDetails?.OrderedQuantity) {
                    contextDispatch({
                        [FastEntryActionTypes.setQuantity]: response.FastLineDetails?.OrderedQuantity,
                    });
                }
                if (price !== response.FastLineDetails?.DiscountedPrice) {
                    contextDispatch({
                        [FastEntryActionTypes.setPrice]: response.FastLineDetails?.DiscountedPrice,
                    });
                }

                return response.FastLineDetails;
            } else {
                if (response && config.module === 'productReceipts' && field === 'product') {
                    resetToDefault();
                }
            }
        } catch (err) {
            console.warn(err);
        } finally {
            contextDispatch({
                [FastEntryActionTypes.setLoading]: false,
            });
        }
    }

    React.useImperativeHandle(
        ref,
        () => ({
            calculateFastLineEntry(details: any): Promise<any | void> {
                return handleFastEntryFieldChanged(
                    {
                        ...details,
                        ProductId: product,
                        PackageId: packageId,
                        MatchType: matchType,
                        KitStatus: (details && details.KitStatus) ? details.KitStatus : config?.params?.KitStatus,
                        BomCode: (details && details.BomCode) ? details.BomCode : config?.params?.BomCode,
                        OrderedQuantity: quantity,
                    },
                    lastChangedField);
            },
            disablePriceField(): void {
                contextDispatch({
                    [FastEntryActionTypes.setPriceDisabled]: true,
                });
            },
            enablePriceField(): void {
                contextDispatch({
                    [FastEntryActionTypes.setPriceDisabled]: false,
                });
            },
            focusProductField,
            focusPurchaseField,
            focusQuantityField,
            focusPriceField,
            resetToDefault
        })
    );

    React.useEffect(
        () => {
            if (isInvalidProduct) {
                setTimeout(resetToDefault, RESET_WAIT); // wait for the completion of other blocking UI operations
            }
        },
        [isInvalidProduct]
    );

    React.useEffect(
        () => {
            if (config.module === 'salesProcessing') {
                handlers.onProductChanged(product);
            }
        },
        [product]
    );

    React.useEffect(
        () => {
            if (!config.disableFastEntry) {
                resetToDefault();
            }
        },
        [config.disableFastEntry]
    );

    // When we are loading, we disable fields, so we can only re-focus if they are enabled.
    React.useEffect(
        () => {
            if (nonProductFieldsDisabled) return;

            switch (lastChangedField) {
                case 'product':
                    if (config.module === 'productReceipts') {
                        focusPurchaseField();

                        return;
                    }
                    focusQuantityField();
                    break;
                case 'purchaseOrder':
                    focusQuantityField();
                    break;
                case 'quantity':
                    focusPriceField();
                    break;
                default:
            }
        },
        [nonProductFieldsDisabled, lastChangedField, focusPurchaseField, focusQuantityField, focusPriceField]
    );

    //Non product fields disabled depends on whether whole component is disabled or it is loading or whether there is no product id.
    React.useEffect(
        () => {
            contextDispatch({
                [FastEntryActionTypes.setNonProductFieldsDisabled]: isLoading || isNull(product) || config.disableFastEntry,
            });
        },
        [isLoading, product, config.disableFastEntry]
    );

    const onApplyCallback = React.useCallback(
        async () => {
            contextDispatch({
                [FastEntryActionTypes.setApply]: false,
            });
            await handleApply({ ...fastLineDetails, OrderedQuantity: quantity, DiscountedPrice: price, ProductId: product, ...((config.module === 'productReceipts') ? { PurchaseOrder: purchaseOrder?.PurchaseOrder, LineNumber: purchaseOrder?.LineNumber } : {}) });
        },
        [fastLineDetails, quantity, price, product, purchaseOrder]
    );

    React.useEffect(
        () => {
            if (!isLoading && apply) {
                onApplyCallback();
            }
        },
        [isLoading, apply, onApplyCallback]
    );

    async function handleQuantityKeyDown(event: React.KeyboardEvent<HTMLInputElement>): Promise<void> {
        if (event.keyCode === ENTER_KEY) {
            await quantityRef?.current?.blur();
            handleApplyEvent();
        }
    }

    async function handleApply(updatedFastLineDetails: any): Promise<void> {
        if (isNull(product)) {
            return;
        }

        try {
            contextDispatch({
                [FastEntryActionTypes.setLoading]: true,
            });

            const OriginalDiscountedPrice = fastLineDetails ? fastLineDetails.DiscountedPrice : 0;
            await handlers.onApply({
                ...updatedFastLineDetails,
                DiscountedPrice: updatedFastLineDetails.DiscountedPrice,
                PriceOverride: updatedFastLineDetails.DiscountedPrice !== OriginalDiscountedPrice,
            });
            contextDispatch({
                [FastEntryActionTypes.setPriceDisabled]: false,
            });
        } catch (err) {
            console.warn(err);
        } finally {
            contextDispatch({
                [FastEntryActionTypes.setLoading]: false,
            });
        }
    }

    const focusProductField = () => {
        setTimeout(
            () => {
                if (productRef.current) {
                    productRef.current.focus();
                }
            },
            FocusFieldTimeout);
    };

    function focusPurchaseField(): void {
        if (purchaseRef?.current) {
            purchaseRef?.current?.focus();
        }
    }

    function focusQuantityField(): void {
        if (quantityRef?.current) {
            quantityRef.current.focus();
            quantityRef.current.select();
        }
    }

    function focusPriceField(): void {
        setTimeout(
            () => {
                if (priceRef?.current) {
                    priceRef?.current.focus();
                    priceRef?.current.select();
                } else if (priceLookupRef?.current) {
                    priceLookupRef?.current.focus();
                    priceLookupRef?.current.select();
                } else {
                    (document?.getElementsByName('FastEntryPrice')[0] as HTMLInputElement)?.select();
                }
            },
            FocusFieldTimeout
        );
    }

    function resetToDefault(donotFocusProduct?: boolean): void {
        contextDispatch({
            [FastEntryActionTypes.resetToDefault]: null,
        });
        if (!donotFocusProduct) focusProductField();
    }

    const handleApplyEvent = () => {
        setTimeout(
            () => {
                contextDispatch({
                    [FastEntryActionTypes.setApply]: true,
                });
            },
            0
        );
    };

    const handleUndoEvent = () => {
        try {
            contextDispatch({
                [FastEntryActionTypes.setLoading]: true,
            });
            handlers?.onUndo?.();
            resetToDefault();

        } catch (err) {
            console.warn(err);
        } finally {
            contextDispatch({
                [FastEntryActionTypes.setLoading]: false,
            });
        }
    };

    const handleProductDetails = ({ ProductId, PackageId, MatchType, Display, Description }: IProductDetails) => {
        contextDispatch({
            [FastEntryActionTypes.setProduct]: ProductId,
            [FastEntryActionTypes.setPackageId]: PackageId,
            [FastEntryActionTypes.setMatchType]: MatchType,
            [FastEntryActionTypes.setProductDisplay]: Display,
            [FastEntryActionTypes.setProductDescription]: Description,
            [FastEntryActionTypes.setPriceDisabled]: false,
            [FastEntryActionTypes.setLastChangedField]: 'product',
        });
        handleFastEntryFieldChanged({ ProductId, PackageId, MatchType }, 'product').catch((err) => { console.warn(err); });
    };

    const handleOnBlurQuantityField = (value) => {
        if (value !== quantity) {

            handleFastEntryFieldChanged(
                {
                    ProductId: product,
                    PackageId: packageId,
                    MatchType: matchType,
                    OrderedQuantity: value,
                    KitStatus: config?.KitStatus,
                    BomCode: config?.BomCode,
                    ...((config.module === 'productReceipts') ? { PurchaseOrder: purchaseOrder?.PurchaseOrder, LineNumber: purchaseOrder?.LineNumber, BuyPrice: price } : {})
                },
                'quantity'
            );
        }
    };

    const lookupOnBlur = (value: string) => {
        if (value !== purchaseOrder?.PurchaseOrder) {
            contextDispatch({
                [FastEntryActionTypes.setPurchaseOrder]: { PurchaseOrder: value, LineNumber: 0 },
            });
        }
    };

    return (
        <Paper className={classes.fastEntryContainer}>
            <div className={classes.fastEntryFields}>
                <div className={classes.productLookup} style={productDisplay ? {} : { paddingBottom: 16 }}>
                    <ProductLookup
                        ref={productRef}
                        Warehouse={config.params?.Warehouse || undefined}
                        CustomerId={config.params?.CustomerId || undefined}
                        size={'medium'}
                        // value={product?.toString()}
                        display={productDisplay}
                        description={productDescription}
                        placeholder=''
                        disabled={isLoading || config.disableFastEntry || config.disableProduct}
                        onSelectedItemChange={(item: any, _) => {
                            if (item) {
                                const ProductId = item?.Details?.ProductId as number || item?.Code || undefined;
                                const PackageId = item?.Details?.PackageId as number ?? undefined;
                                const MatchType = item?.Details?.MatchType as string ?? undefined;

                                if (ProductId !== product || PackageId !== packageId) {
                                    if (ProductId) {
                                        handleProductDetails({ ProductId, PackageId, MatchType, Display: item?.Display, Description: item?.Description });
                                    } else {
                                        resetToDefault(true);
                                    }
                                }
                            } else {
                                resetToDefault(true);
                            }
                        }}
                    />
                </div>
                {
                    config.module === 'productReceipts' && (
                        <BasicLookupActionField
                            className={classes.purchaseOrderLookup}
                            placeholder={''}
                            label={'Purchase order'}
                            size={'large'}
                            searchScreenTitle={'Select Purchase Order'}
                            lookupName={'ProductPurchaseOrderLine'}
                            value={purchaseOrder?.PurchaseOrder}
                            onSelectedItemChange={async (selectedPurchaseOrder: any) => {
                                if (selectedPurchaseOrder) {
                                    const LineNumber = selectedPurchaseOrder?.Details?.LineNumber ?? undefined;
                                    const PurchaseOrder = selectedPurchaseOrder?.Details?.PurchaseOrder ?? undefined;
                                    const response: any = await handleFastEntryFieldChanged(({ ProductId: product, PackageId: packageId, MatchType: matchType, OrderedQuantity: quantity, KitStatus: config?.KitStatus, BomCode: config?.BomCode, DiscountedPrice: price, LineNumber, PurchaseOrder } as any), 'purchaseOrder').catch((err) => { console.warn(err); });
                                    isDirtyPurchaseRefvalue.current.value = response?.PurchaseOrder;
                                    contextDispatch({
                                        [FastEntryActionTypes.setPurchaseOrder]: { PurchaseOrder: response?.PurchaseOrder, LineNumber: response?.PurchaseOrderLineNumber },
                                        [FastEntryActionTypes.setLastChangedField]: 'purchaseOrder',
                                    });
                                }
                            }}
                            suppressDescription={true}
                            disabled={isNull(product)}
                            ref={purchaseRef}
                            descriptionField={'Label'}
                            onEnterKeyDown={(event) => { if (event.key === 'Enter') handleApplyEvent(); }}
                            searchScreenAPIParms={{ Type: 'LOOKUP' }}
                            isProductWarehouseSupplierScoped={true}
                            params={{ Supplier: config.params?.Supplier, ProductId: product, Warehouse: config.params?.Warehouse }}
                            config={{
                                displaySearchContainer: false
                            }}
                            onBlur={lookupOnBlur}
                        />
                    )
                }
                <NumericField
                    label={'Quantity'}
                    size={width < MINIMUMSIZESCREEN ? 'medium' : 'small'}
                    disabled={nonProductFieldsDisabled || isInvalidProduct}
                    className={classes.quantity}
                    value={quantity}
                    onBlur={handleOnBlurQuantityField}
                    ref={quantityRef}
                    onKeyDown={async (e: any) => handleQuantityKeyDown(e)}
                />
                {!config.hidePrice && !config.priceFieldAsLookup && <ExtendedTextField
                    label={config.module === 'productReceipts' ? 'Buy price' : 'Price'}
                    size={'medium'}
                    disabled={nonProductFieldsDisabled || priceDisabled || isInvalidProduct}
                    value={price}
                    fieldRef={(reference) => priceRef = reference}
                    placeholder={'0.00'}
                    decimalSeparator
                    type={'number'}
                    name='FastEntryPrice'
                    helpText={product && fastLineDetails?.PriceDescription !== '' && fastLineDetails?.PriceDescription !== undefined ? fastLineDetails?.PriceDescription : undefined}
                    onChange={(e) => {
                        const value = e.target.value;
                        contextDispatch({
                            [FastEntryActionTypes.setLastChangedField]: 'price',
                            [FastEntryActionTypes.setPrice]: value,
                        });
                    }}
                    onKeyDown={async (e: any) => {
                        if (e.keyCode === ENTER_KEY) {
                            contextDispatch({
                                [FastEntryActionTypes.setPrice]: Number(e.target.value),
                            });
                            handleApplyEvent();
                        }
                    }}
                />}
                {!config.hidePrice && config.priceFieldAsLookup && <CustomerPricingLookup
                    Warehouse={config?.params?.Warehouse}
                    ProductId={product}
                    CustomerId={config?.params?.CustomerId}
                    KeyId={config?.params?.KeyId}
                    TableId={config?.params?.TableId}
                    TaxIncluded={product && fastLineDetails?.TaxIncluded}
                    Quantity={quantity}
                    Description={product && fastLineDetails?.PriceDescription}
                    label={config.module === 'productReceipts' ? 'Buy price' : 'Price'}
                    size={'medium'}
                    disabled={nonProductFieldsDisabled || priceDisabled || isInvalidProduct}
                    value={price.toString()}
                    ref={priceLookupRef}
                    onSelectedItemChange={(item) => {
                        const value = Number(item?.ReturnValue);
                        contextDispatch({
                            [FastEntryActionTypes.setLastChangedField]: 'price',
                            [FastEntryActionTypes.setPrice]: value,
                        });
                    }}
                    onEnterKeyDown={(_) => {
                        handleApplyEvent();
                    }
                    }
                />}
            </div>
            <div className={classes.buttonsContainer}>
                <LoadingButton
                    onMouseDown={handleApplyEvent}
                    onKeyPress={(event) => { if (event.key === 'Enter') handleApplyEvent(); }}
                    disabled={nonProductFieldsDisabled || isInvalidProduct}
                    variant={'contained'}
                    loading={isLoading}
                    startIcon={<CheckCircleIcon style={{ color: 'green' }} />}
                    className={classNames(classes.button, nonProductFieldsDisabled ? classes.disabledBtn : undefined)}>
                    APPLY
                </LoadingButton>
                <Button
                    onClick={handleUndoEvent}
                    onKeyPress={(event) => { if (event.key === 'Enter') handleUndoEvent(); }}
                    disabled={nonProductFieldsDisabled}
                    variant={'contained'}
                    className={classNames(classes.button, nonProductFieldsDisabled ? classes.disabledBtn : undefined)}>
                    <CancelIcon style={{ color: 'red' }} /> UNDO
                </Button>
            </div>
        </Paper >
    );
};

export default (React.forwardRef(FastEntry));
